import React, { useCallback, useEffect, useRef, useState } from "react";
import Card from "components/card";

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { IoEye } from "react-icons/io5";
import Pagination from "components/pagination/Pagination";
import Loader from "../../../assets/img/loader.gif";
import Toast from "components/toaster";
import { FiSearch } from "react-icons/fi";
import EventModal from "./TicketModal"

import { TicketService } from "services";
import tabCache from "utils/tabCache";

const columnHelper = createColumnHelper();

const TicketRevenue = () => {
  const [sorting, setSorting] = useState([]);
  const [data, setData] = useState(tabCache.ticketRevenue?.data || []);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isPageChanging, setIsPageChanging] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState("");

  const debounce = (func, delay) => {
    let timeoutId;
    return function (...args) {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  };

  // Create debounced search handler
  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchText(value);
    }, 500),
    []
  );

  // Handle search input change
  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchInputValue(value);
    debouncedSearch(value);
  };

  const prevSearchTextRef = useRef("");

  /* ===================== API ===================== */
  const getTicketRevenue = async (isSearching = false) => {
    const isFirstLoad = tabCache.ticketRevenue?.firstLoad ?? true;

    if (isFirstLoad || isSearching || isPageChanging) {
      setIsLoading(true);
    }

    try {
      const body = {
        pagination: {
          page: page,
          pageSize: pageSize,
          projection: {},
          filters: {},
          sort: {},
        },
        searchtext: encodeURIComponent(searchText),
      };

      const response = await TicketService.getSoldTickets(body);

      const rawList =
        response?.data?.data ??
        response?.data?.tickets ??
        response?.data?.items ??
        [];

      // Normalise each item so both the table and modal
      // receive a consistent shape regardless of backend keys
      const list = rawList.map((item) => {
        const userRaw = item.user || {};

        // Normalise user email
        const email =
          userRaw.email ||
          userRaw.userEmail ||
          userRaw.emailId ||
          item.email ||
          item.userEmail ||
          item.user_email ||
          null;

        // Normalise username / display name
        const name =
          userRaw.name ||
          userRaw.username ||
          userRaw.userName ||
          item.name ||
          item.username ||
          item.userName ||
          item.user_name ||
          null;

        // Normalise profile image
        const profileImage =
          userRaw.profileImage ||
          userRaw.profilePic ||
          userRaw.avatar ||
          userRaw.image ||
          item.profileImage ||
          item.profilePic ||
          item.avatar ||
          null;

        const account_type =
          userRaw.account_type ||
          userRaw.accountType ||
          userRaw.type ||
          item.userType ||
          null;

        const user = {
          ...userRaw,
          ...(name ? { name } : {}),
          ...(email ? { email } : {}),
          ...(profileImage ? { profileImage } : {}),
          ...(account_type ? { account_type } : {}),
        };

        // Normalise event title
        const eventTitle =
          item.eventTitle ||
          item.event_title ||
          item.eventName ||
          item.event_name ||
          item.event?.title ||
          item.event?.name ||
          item.title ||
          "";

        // Normalise ticket name
        const ticketName =
          item.ticketName ||
          item.ticket_name ||
          item.ticketTitle ||
          item.ticket?.name ||
          item.ticket?.title ||
          item.ticket?.ticketName ||
          item.ticketOption?.title ||
          item.ticketOption?.name ||
          item.ticketOptions?.[0]?.title ||
          item.ticketOptions?.[0]?.name ||
          "";

        const ticketOptions = (item.ticketOptions || item.tickets || []).map((opt) => {
          const scanned =
            opt.scannedQuantity ||
            opt.scanned_quantity ||
            opt.scannedCount ||
            opt.scanned_count ||
            opt.scanned ||
            opt.scannedTickets ||
            0;

          return {
            ...opt,
            scanned,
            total: opt.total || opt.total_quantity || opt.quantity || opt.totalTickets || 0,
            sold: opt.sold || opt.sold_count || opt.buyCount || opt.ticketBuyCount || 0,
            title: opt.title || opt.name || opt.ticket_name || opt.ticketName || "--",
            price: opt.price ?? opt.ticketPrice ?? 0,
            revenue: opt.revenue || opt.totalRevenue || ((opt.sold || 0) * (opt.price || 0)) || 0
          };
        });

        // Sum scanned quantity from options if not at top level
        const summedScanned = ticketOptions.reduce((acc, opt) => acc + (opt.scanned || 0), 0);

        return {
          ...item,
          user,
          title: eventTitle,
          eventTitle,
          ticketName,
          ticketPrice:
            item.price ||
            item.ticketPrice ||
            item.ticket_price ||
            item.ticket?.price ||
            item.ticketOption?.price ||
            item.ticketOptions?.[0]?.price ||
            item.tickets?.[0]?.price ||
            item.amount ||
            0,
          ticketBuyCount:
            item.totalTickets ||
            item.total_quantity ||
            item.quantity ||
            item.totalItems ||
            0,
          totalRevenue:
            item.totalRevenue ||
            item.revenue ||
            item.amount ||
            item.totalAmount ||
            0,
          ticketsSold:
            item.ticketsSold ||
            item.sold ||
            item.ticketBuyCount ||
            0,
          startDate:
            item.startdate ||
            item.event?.startdate ||
            item.event?.startDate ||
            item.event?.start_date ||
            item.startDate ||
            item.start_date ||
            item.eventStartDate ||
            item.createdAt ||
            null,
          endDate:
            item.enddate ||
            item.event?.enddate ||
            item.event?.endDate ||
            item.event?.end_date ||
            item.endDate ||
            item.end_date ||
            item.eventEndDate ||
            null,
          ticketOptions,
          status:
            item.status ||
            item.ticketStatus ||
            item.paymentStatus ||
            item.state ||
            null,
          location: (() => {
            const loc =
              item.location ||
              item.event?.location ||
              item.event_location ||
              item.address ||
              item.event?.address ||
              null;
            if (typeof loc === "string") return loc;
            if (typeof loc === "object" && loc !== null) {
              return loc.address || (loc.coordinates ? `Lat: ${loc.coordinates[1]}, Lng: ${loc.coordinates[0]}` : JSON.stringify(loc));
            }
            return null;
          })(),
          description:
            item.description ||
            item.event?.description ||
            item.event_description ||
            null,
          ticketDescription:
            item.ticketDescription ||
            item.ticket?.description ||
            item.ticketOption?.description ||
            item.ticket_description ||
            null,
          amount:
            item.transaction?.amount ||
            item.transaction?.transaction_amount ||
            item.ticket?.transaction?.amount ||
            item.totalRevenue ||
            item.revenue ||
            item.amount ||
            item.totalAmount ||
            item.total_amount ||
            0,
          transactionId:
            item.transaction?._id ||
            item.transaction?.id ||
            item.transactionId ||
            item.ticket?.transaction?._id ||
            null,
          paymentStatus:
            item.transaction?.payment_status ||
            item.transaction?.status ||
            item.ticket?.transaction?.payment_status ||
            item.paymentStatus ||
            item.payment_status ||
            item.status ||
            null,
          scannedQuantity:
            item.scannedQuantity ||
            item.scanned_quantity ||
            item.scannedCount ||
            item.scanned_tickets ||
            item.ticket?.scannedQuantity ||
            item.ticket?.scanned_quantity ||
            item.ticket?.scanned_count ||
            item.ticketOption?.scannedQuantity ||
            item.ticketOption?.scanned_quantity ||
            item.ticketOption?.scanned_count ||
            summedScanned ||
            0,
          eventImage:
            item.event?.images?.[0] ||
            item.event?.image ||
            item.images?.[0] ||
            item.image ||
            null,
          serviceFee:
            item.transaction?.serviceFee ||
            item.transaction?.service_fee ||
            item.serviceFee ||
            item.service_fee ||
            0,
          adminFee:
            item.transaction?.adminFee ||
            item.transaction?.admin_fee ||
            item.adminFee ||
            item.admin_fee ||
            0,
        };
      });

      const totalCount =
        response?.data?.pagination?.total ??
        response?.data?.pagination?.totalCount ??
        response?.data?.totalCount ??
        response?.data?.totaltickets ??
        response?.data?.totalTickets ??
        response?.data?.totalItems ??
        response?.data?.total ??
        response?.data?.count ??
        list.length;

      setData(list);
      tabCache.ticketRevenue = {
        data: list,
        firstLoad: false,
      };

      let computedTotalPages = 1;
      if (response?.data?.pagination?.pages) {
        computedTotalPages = response.data.pagination.pages;
      } else if (response?.data?.totalPages) {
        computedTotalPages = response.data.totalPages;
      } else {
        computedTotalPages = Math.max(1, Math.ceil(totalCount / pageSize));
      }

      setTotalPages(computedTotalPages);
    } catch (error) {
      Toast("error", error?.response?.data?.message || "Something went wrong");
      setData([]);
    } finally {
      setIsLoading(false);
      setIsPageChanging(false);
    }
  };

  useEffect(() => {
    const isSearchChange = prevSearchTextRef.current !== searchText;

    if (isSearchChange) {
      prevSearchTextRef.current = searchText;
    }

    getTicketRevenue(isSearchChange);
    // eslint-disable-next-line
  }, [page, searchText]);

  /* ===================== COLUMNS ===================== */

  const [isRevenueModalOpen, setIsRevenueModalOpen] = useState(false);
  const [selectedRevenue, setSelectedRevenue] = useState(null);

  const handleViewRevenue = (rowData) => {
    setSelectedRevenue(rowData);
    setIsRevenueModalOpen(true);
  };

  const columns = [
    // USERNAME
    columnHelper.accessor(
      (row) =>
        row.user?.name ||
        row.username ||
        row.userName ||
        row.user_name ||
        row.name ||
        "",
      {
        id: "username",
        header: () => (
          <p className="text-sm font-bold  uppercase text-white">
            Username
          </p>
        ),
        cell: (info) => (
          <p className="text-sm font-bold text-white">
            {info.getValue() || "--"}
          </p>
        ),
      }
    ),

    // USER EMAIL
    columnHelper.accessor(
      (row) =>
        row.user?.email ||
        row.email ||
        row.userEmail ||
        row.user_email ||
        "",
      {
        id: "userEmail",
        header: () => (
          <p className="text-sm font-bold pl-8 uppercase text-white">
            User Email
          </p>
        ),
        cell: (info) => (
          <p className="text-sm font-bold text-white">
            {info.getValue() || "--"}
          </p>
        ),
      }
    ),

    // EVENT TITLE
    columnHelper.accessor(
      (row) => row.eventTitle || row.title || "",
      {
        id: "title",
        header: () => (
          <p className="text-sm font-bold pl-8 uppercase text-white">
            Event Title
          </p>
        ),
        cell: (info) => (
          <p className="text-sm font-bold text-white">
            {info.getValue() || "--"}
          </p>
        ),
      }
    ),


    // TICKET QUANTITY
    columnHelper.accessor((row) => row.ticketBuyCount ?? 0, {
      id: "ticketQuantity",
      header: () => (
        <p className="text-sm font-bold pl-8 uppercase text-white">
          Ticket Purchased
        </p>
      ),
      cell: (info) => (
        <p className="text-sm ml-10 font-bold text-white">
          {info.getValue() ?? 0}
        </p>
      ),
    }),

    // TICKETS SOLD / BUY COUNT
    // columnHelper.accessor((row) => row.ticketsSold ?? row.ticketBuyCount ?? 0, {
    //   id: "ticketsSold",
    //   header: () => (
    //     <p className="text-sm font-bold pl-8 uppercase text-white">
    //       Tickets Purchased
    //     </p>
    //   ),
    //   cell: (info) => (
    //     <p className="text-sm font-bold text-white">
    //       {info.getValue() ?? 0}
    //     </p>
    //   ),
    // }),


    // ACTION
    columnHelper.display({
      id: "action",
      header: () => (
        <p className="pl-4 text-sm font-bold uppercase text-white">Action</p>
      ),
      cell: ({ row }) => (
        <div className="flex justify-start">
          <div
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg bg-[#369FFF1A]/10 text-[#369FFF] "
            onClick={() => handleViewRevenue(row.original)}
          >
            <IoEye />
          </div>
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  /* ===================== UI ===================== */
  return (
    <div className="mt-3 grid h-full grid-cols-1 gap-5">
      <div className="col-span-1 h-full w-full rounded-xl">
        <Card extra="w-full h-fit pb-6  -px-4 relative">
          {/* SEARCH */}
          <div className="flex items-center mt-4 ml-4">
            <div className="flex items-center rounded-full bg-navy-900 py-3 text-white w-[180px]">
              <FiSearch className="ml-3 h-4 w-4" />
              <input
                className="search-input-field block w-full ml-2 flex-1 bg-navy-900 text-sm font-medium text-white outline-none placeholder:!text-white"
                placeholder="Search..."
                onChange={handleSearchInputChange}
                value={searchInputValue}
              />
            </div>
          </div>

          {/* TABLE (EXACT SAME AS REPORTS SCROLL) */}
          <div
            className={`table-scroll mt-6   relative w-full ${isLoading ? "h-[65vh] overflow-hidden" : "overflow-x-auto"
              }`}
          >
            {isLoading && (
              <div className="pointer-events-none absolute inset-0 z-40 flex items-center justify-center">
                <img src={Loader} className="h-16 w-16" alt="Loading" />
              </div>
            )}

            <table
              className={`z-10 min-w-[1100px] w-full ${isLoading ? "table-fixed h-full" : ""
                }`}
              style={
                isLoading
                  ? { tableLayout: "fixed", width: "100%", height: "100%" }
                  : { tableLayout: "auto" }
              }
            >
              <thead className={isLoading ? "hidden" : ""}>
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id}>
                    {hg.headers.map((header, index) => (
                      <th
                        key={header.id}
                        className="whitespace-nowrap border-b pl-6 border-[#fbfbfb1a] pb-2 pt-4 px-0 text-left"
                      >

                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody className={isLoading ? "h-full" : ""}>
                {!isLoading && table.getRowModel().rows.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr
                      key={row.id}
                      className={`group hover:bg-[#8E8E8E]/5 ${row.index === table.getRowModel().rows.length - 1
                        ? ""
                        : "border-b border-[#fbfbfb1a]"
                        }`}
                    >

                      {row.getVisibleCells().map((cell, index) => (
                        <td
                          key={cell.id}
                          className={`whitespace-nowrap py-3 ${index === 0 ? "pl-6 pr-16" : "pl-14 pr-24"
                            }`}
                        >

                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : !isLoading ? (
                  <tr>
                    <td colSpan={columns.length} className="py-4 text-center">
                      <p className="my-3">Data Not Found</p>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          {/* PAGINATION */}
          {table.getRowModel().rows.length > 0 && !isLoading && (
            <Pagination
              page={page}
              totalPages={totalPages}
              handlePageChange={(p) => {
                setIsPageChanging(true);
                setPage(p);
              }}
            />
          )}
        </Card>
      </div>
      <EventModal
        isOpen={isRevenueModalOpen}
        onClose={() => setIsRevenueModalOpen(false)}
        revenueData={selectedRevenue}
      />

    </div>
  );
};

export default TicketRevenue;
